import React from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { EmployeeSummary } from './types';

interface SummaryTableProps {
  data: EmployeeSummary[];
  onViewHistory: (employee: EmployeeSummary) => void;
  onShowDetails: (employee: EmployeeSummary, type: 'sign-ons' | 'sign-offs') => void;
}

const getStatusBadge = (status: string) => {
  const statusStyles: Record<string, string> = {
    'on-vessel': 'bg-green-100 text-green-800',
    'on-vacation': 'bg-blue-100 text-blue-800',
    'off-duty': 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || ''}`}>
      {status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
    </span>
  );
};

const SummaryTable: React.FC<SummaryTableProps> = ({ data, onShowDetails }) => {
  const columns: TableColumn[] = [
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
      render: (value: string, item: EmployeeSummary) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{item.rank}</div>
        </div>
      ),
    },
    {
      key: 'totalSignOns',
      label: 'Total Sign Ons',
      sortable: true,
      render: (value: number, item: EmployeeSummary) => (
        <div className="text-center">
          <button
            onClick={() => onShowDetails(item, 'sign-ons')}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-800 text-sm font-medium hover:bg-green-200 transition-colors cursor-pointer"
            title="Click to view details"
          >
            {value}
          </button>
        </div>
      ),
    },
    {
      key: 'totalSignOffs',
      label: 'Total Sign Offs',
      sortable: true,
      render: (value: number, item: EmployeeSummary) => (
        <div className="text-center">
          <button
            onClick={() => onShowDetails(item, 'sign-offs')}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-800 text-sm font-medium hover:bg-red-200 transition-colors cursor-pointer"
            title="Click to view details"
          >
            {value}
          </button>
        </div>
      ),
    },
    {
      key: 'totalVacations',
      label: 'Total Vacations',
      sortable: true,
      render: (value: number) => (
        <div className="text-center">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
            {value}
          </span>
        </div>
      ),
    },
    {
      key: 'currentStatus',
      label: 'Current Status',
      sortable: true,
      render: (value: string, item: EmployeeSummary) => (
        <div>
          {getStatusBadge(value)}
          {item.currentVessel && (
            <div className="text-xs text-gray-500 mt-1">{item.currentVessel}</div>
          )}
        </div>
      ),
    },
    {
      key: 'lastActivity',
      label: 'Last Activity',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  return (
    <div className="card">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Employee Summary</h2>
        <p className="text-sm text-gray-600">Overview of each employee's activity and current status</p>
      </div>
      <Table data={data} columns={columns} searchable={false} />
    </div>
  );
};

export default SummaryTable;
